<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { SidepanelContent, Alert } from '@Shelly/shelly-design-system';
	import { isExperimentalEnabled } from 'src/helpers/user_settings_helpers';
	// Scene preview components
	import WhenPreview from 'src/components/Scenes/SceneDrawSections/WhenPreview.svelte';
	import DoPreview from 'src/components/Scenes/SceneDrawSections/DoPreview.svelte';
	import ElsePreview from 'src/components/Scenes/SceneDrawSections/ElsePreview.svelte';
	import ActiveTimePreview from 'src/components/Scenes/SceneDrawSections/ActiveTimePreview.svelte';
	import MorePreview from 'src/components/Scenes/SceneDrawSections/MorePreview.svelte';
	import NotificationRecipientsPreview from 'src/components/Scenes/SceneDrawSections/NotificationRecipientsPreview.svelte';
	import SceneSection from 'src/components/Scenes/SceneDrawSections/SceneSection.svelte';
	import { ScriptSceneSection } from '@Shelly/scenes-script';
	import { summariseSceneNotificationRecipients } from 'src/components/Scenes/helpers/notification_recipients_helpers';

	export let id;
	export let scene;
	export let whens = [];
	export let thens = [];
	export let elses = [];
	export let active_time = [];
	export let run_on_save = false;
	export let scene_enable_val = false;
	export let allowed_for_3rd_party = false;
	export let duplicateInfo = null;

	const dispatch = createEventDispatcher();

	/**
	 * FE-1682: Only render the recipients section when the scene has at least one
	 * push or mail notification action; otherwise the section would be empty and noisy.
	 */
	$: notificationSummary =
		typeof scene === 'object' ? summariseSceneNotificationRecipients(scene) : {};
	$: hasNotificationRecipients = !!notificationSummary.push || !!notificationSummary.mail;

	$: sections =
		typeof scene === 'object'
			? [
					{
						slot_component: WhenPreview,
						slot_props: { props: { whens, slot: 'content' } },
						props: { text: $format('title_when'), _class: 'mb-s' },
					},
					{
						slot_component: DoPreview,
						slot_props: { props: { thens, slot: 'content' } },
						props: { text: $format('title_do'), _class: 'mb-s mt-m' },
					},
					...(elses.length > 0
						? [
								{
									slot_component: ElsePreview,
									slot_props: { props: { elses, slot: 'content' } },
									props: { text: 'Else', _class: 'mb-s mt-m' },
								},
							]
						: []),
					{
						slot_component: ActiveTimePreview,
						slot_props: {
							props: { slot: 'content', active_time },
						},
						props: {
							text: $format('title_active_time'),
							_class: 'mb-s mt-m',
						},
					},
					...(hasNotificationRecipients
						? [
								{
									slot_component: NotificationRecipientsPreview,
									slot_props: { props: { slot: 'content', scene } },
									props: {
										text: $format('scene_notification_recipient_section_title', {
											default: 'Notification recipients',
										}),
										_class: 'mb-s mt-m',
									},
								},
							]
						: []),
					{
						slot_component: MorePreview,
						slot_props: {
							props: {
								slot: 'content',
								run_on_save,
								allowed_for_3rd_party,
								scene_enable_val,
							},
						},
						props: { text: $format('title_more'), _class: 'mb-s mt-m' },
					},
				]
			: [];
</script>

<SidepanelContent>
	<!-- Duplicate Scene Alert -->
	{#if duplicateInfo}
		<Alert
			type="info"
			class="mb-m"
			heading={$format('scene_duplicate_alert_heading', {
				default: 'Duplicate Scene Detected',
			})}
			message={$format('scene_duplicate_alert_title', {
				values: { original_name: duplicateInfo.originalName },
				default: `Scene is identical to "${duplicateInfo.originalName}"`,
			})}
		>
			{$format('scene_duplicate_alert_message', {
				default: `This scene has the same conditions and actions as the original.`,
			})}
		</Alert>
	{/if}

	{#if isExperimentalEnabled('inf2') && scene?.infinno}
		<ScriptSceneSection
			{scene}
			activeTimeComponent={ActiveTimePreview}
			morePreviewComponent={MorePreview}
			on:sceneConvertedToScript={(e) => dispatch('scene-converted-to-script', e.detail)}
		/>
	{:else}
		{#key sections}
			<div>
				{#each sections as section}
					<SceneSection {...section.props}>
						<svelte:component
							this={section.slot_component}
							on:edit-when={(e) => dispatch('edit-when', e.detail)}
							on:edit-do={(e) => dispatch('edit-do', e.detail)}
							on:edit-active-time={(e) => dispatch('edit-active-time', e.detail)}
							{...section.slot_props.props}
						/>
					</SceneSection>
				{/each}
			</div>
		{/key}
	{/if}
</SidepanelContent>
